import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  Collapse,
  Chip,
  IconButton,
  Fade,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { formatCurrency, formatInvoiceNumber } from '../utils/registerPaymentUtils';
import { MESSAGES } from '../constants/registerPaymentConstants';

const SaveCollectionModal = ({
  open,
  onClose,
  loading,
  result,
  error,
  setTableData,
  selectedSalePoint,
  onNavigateToPayments,
}) => {
  const [expandedCoupons, setExpandedCoupons] = useState(false);
  const [expandedInvoices, setExpandedInvoices] = useState(false);

  const handleClose = () => {
    onClose();
    if (result && !error) {
      onNavigateToPayments();
    }
  };

  const toggleCouponsExpansion = () => {
    setExpandedCoupons(!expandedCoupons);
  };

  const toggleInvoicesExpansion = () => {
    setExpandedInvoices(!expandedInvoices);
  };

  const renderCouponsSection = () => {
    if (!result?.updated_coupons?.length) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            p: 1,
            borderRadius: '8px',
            backgroundColor: '#e8f5e8',
            '&:hover': { backgroundColor: '#d4edda' },
          }}
          onClick={toggleCouponsExpansion}
        >
          <ReceiptIcon sx={{ mr: 1, color: 'success.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
            Cupones Registrados ({result.updated_coupons.length})
          </Typography>
          <IconButton size="small">
            {expandedCoupons ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expandedCoupons}>
          <Box
            sx={{
              textAlign: 'left',
              maxHeight: '300px',
              overflow: 'auto',
              mt: 1,
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              p: 2,
            }}
          >
            {result.updated_coupons.map((coupon, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  mb: 1,
                  backgroundColor: '#f0f8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  border: '1px solid #c8e6c9',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Cupón #{coupon.code_number}
                  </Typography>
                  <Chip
                    label={`Cuota ${coupon.fee_number1}${
                      coupon.fee_number2 ? `-${coupon.fee_number2}` : ''
                    }`}
                    size="small"
                    color="primary"
                  />
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1,
                    fontSize: '13px',
                  }}
                >
                  <Box>
                    • <strong>Importe:</strong> {formatCurrency(coupon.first_due_amount)}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  };

  const renderInvoicesSection = () => {
    if (!result?.invoices?.length) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            p: 1,
            borderRadius: '8px',
            backgroundColor: '#e3f2fd',
            '&:hover': { backgroundColor: '#bbdefb' },
          }}
          onClick={toggleInvoicesExpansion}
        >
          <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
            Facturas Generadas ({result.invoices.length})
          </Typography>
          <IconButton size="small">
            {expandedInvoices ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expandedInvoices}>
          <Box
            sx={{
              textAlign: 'left',
              maxHeight: '300px',
              overflow: 'auto',
              mt: 1,
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              p: 2,
            }}
          >
            {result.invoices.map((invoice, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  mb: 1,
                  backgroundColor: '#f0f4ff',
                  borderRadius: '8px',
                  fontSize: '14px',
                  border: '1px solid #c5cae9',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Factura #{' '}
                    {formatInvoiceNumber(
                      invoice.type_letter,
                      selectedSalePoint?.number,
                      invoice.receipt_number
                    )}
                  </Typography>
                  <Chip label={formatCurrency(invoice.to_total)} 
                  size='small'
                  color='primary'
                  variant='outlined' />
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1,
                    fontSize: '13px',
                  }}
                >
                  <Box>
                    • <strong>Total:</strong> {formatCurrency(invoice.to_total)}
                    <br />
                    {invoice.cae && (
                      <>
                        • <strong>CAE:</strong> {invoice.cae}
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  };

  const renderErrorsSection = () => {
    if (!result?.errors?.length) return null;

    return (
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: 'warning.main',
            mb: 2,
          }}
        >
          ⚠️ Algunos cupones tuvieron errores
        </Box>

        <Box sx={{ textAlign: 'left', maxHeight: '200px', overflow: 'auto' }}>
          {result.errors.map((error, index) => (
            <Box
              key={index}
              sx={{
                p: 2,
                mb: 1,
                backgroundColor: '#fff3cd',
                borderRadius: '8px',
                fontSize: '14px',
                border: '1px solid #ffeaa7',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 'bold', color: 'warning.dark', mb: 1 }}
              >
                Error en cupón: {error.uuid_coupon}
              </Typography>
              <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                {error.mensaje || error.message || 'Error desconocido'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? handleClose : undefined}
      maxWidth="md"
      fullWidth={true}
      disableEscapeKeyDown={loading}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '10px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
        },
      }}
    >
      <DialogContent sx={{ p: 4, textAlign: 'center' }}>
        {loading && (
          <Fade in timeout={300}>
            <Box>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>
                Guardando cobranza...
              </Typography>
              <Typography sx={{ mt: 1, color: 'text.secondary' }}>
                Por favor espere mientras procesamos los cupones
              </Typography>
            </Box>
          </Fade>
        )}

        {!loading && result && (
          <Fade in timeout={500}>
            <Box>
              {result.updated_coupons?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: 'success.main',
                      mb: 2,
                    }}
                  >
                    {MESSAGES.SUCCESS.SAVE_COLLECTION}
                  </Box>

                  <Box
                    sx={{
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      p: 2,
                      mb: 2,
                    }}
                  >
                    <Box sx={{ fontWeight: 'bold', mb: 1 }}>📊 Resumen:</Box>
                    <Box>• Cupones procesados: {result.updated_coupons.length}</Box>
                    <Box>• Facturas generadas: {result.invoices?.length || 0}</Box>
                    <Box>• Errores: {result.errors?.length || 0}</Box>
                  </Box>

                  {renderCouponsSection()}
                </Box>
              )}

              {renderInvoicesSection()}
              {renderErrorsSection()}
            </Box>
          </Fade>
        )}

        {!loading && error && (
          <Fade in timeout={500}>
            <Box>
              <Box
                sx={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: 'error.main',
                  mb: 2,
                }}
              >
                {MESSAGES.ERROR.SAVE_COLLECTION}
              </Box>
              <Box
                sx={{
                  backgroundColor: '#ffebee',
                  borderRadius: '8px',
                  p: 2,
                  color: 'error.dark',
                }}
              >
                {error.message || 'Ocurrió un error inesperado'}
              </Box>
            </Box>
          </Fade>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
        {!loading && (
          <>
            {result && !error && (
              <Button
                onClick={handleClose}
                variant="contained"
                size="large"
                sx={{
                  minWidth: '120px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              >
                Ver cobros
              </Button>
            )}
            <Button
              onClick={() => {
                onClose();
                setTableData([]);
              }}
              variant="outlined"
              size="large"
              sx={{
                minWidth: '120px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              Cerrar
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SaveCollectionModal;
